    import React, { useState, useEffect } from 'react';
import apiClient from '../../api';
import './PanditApprovalManagement.css';

function PanditApprovalManagement() {
  const [activeTab, setActiveTab] = useState('pending');
  const [pandits, setPandits] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(false);
  const [actionModal, setActionModal] = useState({ isOpen: false, pandit: null, action: null });
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    loadStats();
    loadPandits(activeTab);
  }, [activeTab]);

  const loadStats = async () => {
    try {
      const response = await apiClient.get('/api/pandit/admin/approval-stats/');
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadPandits = async (status) => {
    setLoading(true);
    try {
      let response;
      if (status === 'pending') {
        response = await apiClient.get('/api/pandit/admin/pending/');
      } else {
        response = await apiClient.get(`/api/pandit/admin/status/${status}/`);
      }
      setPandits(response.data.results || response.data.pandits || []);
    } catch (error) {
      console.error('Error loading pandits:', error);
      setPandits([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = (pandit, action) => {
    setActionModal({ isOpen: true, pandit, action });
    setRejectionReason('');
  };

  const confirmAction = async () => {
    if (actionModal.action === 'reject' && !rejectionReason.trim()) {
      alert('कृपया अस्वीकृति का कारण दर्ज करें / Please provide a rejection reason');
      return;
    }

    try {
      const payload = {
        approval_status: actionModal.action === 'approve' ? 'approved' : 'rejected'
      };
      
      if (actionModal.action === 'reject') {
        payload.rejection_reason = rejectionReason;
      }

      const panditId = actionModal.pandit._id || actionModal.pandit.id;
      if (!panditId) {
        alert('त्रुटि: पंडित ID नहीं मिला / Error: Pandit ID not found');
        return;
      }
      
      await apiClient.post(`/api/pandit/admin/approve/${panditId}/`, payload);
      
      // Reload data
      await loadStats();
      await loadPandits(activeTab);
      
      // Close modal
      setActionModal({ isOpen: false, pandit: null, action: null });
      setRejectionReason('');
      
      alert(`पंडित ${actionModal.action === 'approve' ? 'स्वीकृत' : 'अस्वीकृत'} किया गया / Pandit ${actionModal.action === 'approve' ? 'approved' : 'rejected'} successfully`);
    } catch (error) {
      console.error('Error performing action:', error);
      alert('त्रुटि / Error: ' + (error.response?.data?.error || error.message));
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { text: 'लंबित / Pending', color: '#FF9800', emoji: '⏳' },
      approved: { text: 'स्वीकृत / Approved', color: '#4CAF50', emoji: '✅' },
      rejected: { text: 'अस्वीकृत / Rejected', color: '#F44336', emoji: '❌' }
    };
    const badge = badges[status] || badges.pending;
    return (
      <span style={{ 
        backgroundColor: badge.color, 
        color: 'white', 
        padding: '5px 10px', 
        borderRadius: '15px',
        fontSize: '12px',
        fontWeight: 'bold'
      }}>
        {badge.emoji} {badge.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  return (
    <div className="pandit-approval-container">
      <div className="approval-header">
        <h1>🙏 पंडित अनुमोदन प्रबंधन / Pandit Approval Management</h1>
        
        <div className="stats-row">
          <div className="stat-box total">
            <div className="stat-number">{stats.total}</div>
            <div className="stat-label">कुल / Total</div>
          </div>
          <div className="stat-box pending">
            <div className="stat-number">{stats.pending}</div>
            <div className="stat-label">⏳ लंबित / Pending</div>
          </div>
          <div className="stat-box approved">
            <div className="stat-number">{stats.approved}</div>
            <div className="stat-label">✅ स्वीकृत / Approved</div>
          </div>
          <div className="stat-box rejected">
            <div className="stat-number">{stats.rejected}</div>
            <div className="stat-label">❌ अस्वीकृत / Rejected</div>
          </div>
        </div>
      </div>

      <div className="tabs-container">
        <button 
          className={`tab-button ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveTab('pending')}
        >
          ⏳ लंबित / Pending ({stats.pending})
        </button>
        <button 
          className={`tab-button ${activeTab === 'approved' ? 'active' : ''}`}
          onClick={() => setActiveTab('approved')}
        >
          ✅ स्वीकृत / Approved ({stats.approved})
        </button>
        <button 
          className={`tab-button ${activeTab === 'rejected' ? 'active' : ''}`}
          onClick={() => setActiveTab('rejected')}
        >
          ❌ अस्वीकृत / Rejected ({stats.rejected})
        </button>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>लोड हो रहा है... / Loading...</p>
        </div>
      ) : (
        <div className="pandits-grid">
          {pandits.length === 0 ? (
            <div className="no-data">
              <p>कोई पंडित नहीं मिला / No pandits found</p>
            </div>
          ) : (
            pandits.map((pandit) => (
              <div key={pandit._id} className="pandit-card">
                <div className="pandit-image">
                  {pandit.image ? (
                    <img src={pandit.image} alt={pandit.name} />
                  ) : (
                    <div className="no-image">👤</div>
                  )}
                </div>
                
                <div className="pandit-info">
                  <h3>{pandit.name}</h3>
                  
                  {pandit.description && (
                    <div className="pandit-description">
                      <strong>📝 Description:</strong>
                      <p>{pandit.description}</p>
                    </div>
                  )}
                  
                  <p><strong>📍 Location:</strong> {pandit.location}</p>
                  <p><strong>📞 Phone:</strong> {pandit.phone || 'N/A'}</p>
                  <p><strong>💰 Fees:</strong> ₹{pandit.fees || 'N/A'}</p>
                  <p><strong>📧 Email:</strong> {pandit.email || 'N/A'}</p>
                  <p><strong>📅 Created:</strong> {formatDate(pandit.created_at)}</p>
                  
                  <div className="status-badge">
                    {getStatusBadge(pandit.approval_status)}
                  </div>
                  
                  {pandit.approved_at && (
                    <p className="timestamp"><strong>✅ Approved:</strong> {formatDate(pandit.approved_at)}</p>
                  )}
                  {pandit.rejected_at && (
                    <p className="timestamp"><strong>❌ Rejected:</strong> {formatDate(pandit.rejected_at)}</p>
                  )}
                  {pandit.rejection_reason && (
                    <p className="rejection-reason"><strong>कारण / Reason:</strong> {pandit.rejection_reason}</p>
                  )}
                </div>

                {pandit.approval_status === 'pending' && (
                  <div className="action-buttons">
                    <button 
                      className="approve-btn"
                      onClick={() => handleAction(pandit, 'approve')}
                    >
                      ✅ स्वीकृत करें / Approve
                    </button>
                    <button 
                      className="reject-btn"
                      onClick={() => handleAction(pandit, 'reject')}
                    >
                      ❌ अस्वीकृत करें / Reject
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {actionModal.isOpen && (
        <div className="modal-overlay" onClick={() => setActionModal({ isOpen: false, pandit: null, action: null })}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>
              {actionModal.action === 'approve' ? '✅ स्वीकृत करें / Approve' : '❌ अस्वीकृत करें / Reject'} पंडित
            </h2>
            <p><strong>Name:</strong> {actionModal.pandit?.name}</p>
            <p><strong>Location:</strong> {actionModal.pandit?.location}</p>
            
            {actionModal.action === 'reject' && (
              <div className="rejection-input">
                <label>अस्वीकृति का कारण / Rejection Reason *</label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="कृपया अस्वीकृति का कारण बताएं / Please provide reason for rejection"
                  rows="4"
                />
              </div>
            )}
            
            <div className="modal-actions">
              <button 
                className={actionModal.action === 'approve' ? 'approve-btn' : 'reject-btn'}
                onClick={confirmAction}
              >
                पुष्टि करें / Confirm
              </button>
              <button 
                className="cancel-btn"
                onClick={() => setActionModal({ isOpen: false, pandit: null, action: null })}
              >
                रद्द करें / Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PanditApprovalManagement;
